import { useState, useEffect } from 'react';

import { logger } from '../utils/logger';

import type { FormField } from '../types/pdf.types';

/**
 * Map PDF font names to web-safe font families
 *
 * PDF uses abbreviated font names in defaultAppearance strings (DA):
 * - /Helv → Helvetica
 * - /Times or /TiRo → Times-Roman
 * - /Cour → Courier
 * - /ZaDb → ZapfDingbats
 * - /Symb → Symbol
 *
 * Supports all 14 standard PDF fonts defined in ISO 32000:
 * Helvetica, Times-Roman, Courier (regular, bold, italic/oblique), Symbol, ZapfDingbats
 *
 * @param pdfFontName - Font name from PDF defaultAppearance (e.g., "Helv", "Times", "Cour")
 * @returns CSS font-family stack with web-safe fallbacks
 */
const mapPDFFont = (pdfFontName: string): string => {
  const fontName = pdfFontName.toLowerCase();

  if (fontName.includes('helv') || fontName.includes('arial')) {
    if (fontName.includes('bold') && fontName.includes('oblique')) {
      return 'Helvetica-BoldOblique, "Arial Bold Italic", Arial, sans-serif';
    }
    if (fontName.includes('bold')) {
      return 'Helvetica-Bold, "Arial Bold", Arial, sans-serif';
    }
    if (fontName.includes('oblique') || fontName.includes('italic')) {
      return 'Helvetica-Oblique, "Arial Italic", Arial, sans-serif';
    }
    return 'Helvetica, Arial, sans-serif';
  }

  if (
    fontName.includes('times') ||
    fontName.includes('roman') ||
    fontName.includes('tiro')
  ) {
    if (fontName.includes('bold') && fontName.includes('italic')) {
      return 'Times-BoldItalic, "Times New Roman Bold Italic", "Times New Roman", serif';
    }
    if (fontName.includes('bold')) {
      return 'Times-Bold, "Times New Roman Bold", "Times New Roman", serif';
    }
    if (fontName.includes('italic')) {
      return 'Times-Italic, "Times New Roman Italic", "Times New Roman", serif';
    }
    return 'Times-Roman, "Times New Roman", Times, serif';
  }

  if (fontName.includes('cour') || fontName.includes('mono')) {
    if (
      fontName.includes('bold') &&
      (fontName.includes('oblique') || fontName.includes('italic'))
    ) {
      return 'Courier-BoldOblique, "Courier New Bold Italic", "Courier New", monospace';
    }
    if (fontName.includes('bold')) {
      return 'Courier-Bold, "Courier New Bold", "Courier New", monospace';
    }
    if (fontName.includes('oblique') || fontName.includes('italic')) {
      return 'Courier-Oblique, "Courier New Italic", "Courier New", monospace';
    }
    return 'Courier, "Courier New", monospace';
  }

  if (fontName.includes('symbol')) {
    return 'Symbol, serif';
  }
  if (fontName.includes('zapf') || fontName.includes('zadb')) {
    return 'ZapfDingbats, "Zapf Dingbats", serif';
  }

  return 'Helvetica, Arial, sans-serif';
};

/**
 * PDF field flag mapping (ISO 32000 / PDF.js constants)
 * Prefer direct PDF.js properties when present; fall back to fieldFlags bitmask.
 *
 * readOnly -> READONLY (0x0000001)
 * required -> REQUIRED (0x0000002)
 * noExport -> NOEXPORT (0x0000004)
 * multiline -> MULTILINE (0x0001000)
 * password -> PASSWORD (0x0002000)
 * isCombo -> COMBO (0x0020000)
 * editable -> EDIT (0x0040000)
 * multiSelect -> MULTISELECT (0x0200000)
 * doNotSpellCheck -> DONOTSPELLCHECK (0x0400000)
 * doNotScroll -> DONOTSCROLL (0x0800000)
 * comb -> COMB (0x1000000)
 * richText -> RICHTEXT (0x2000000)
 */
const AnnotationFieldFlag = {
  READONLY: 0x0000001,
  REQUIRED: 0x0000002,
  NOEXPORT: 0x0000004,
  MULTILINE: 0x0001000,
  PASSWORD: 0x0002000,
  COMBO: 0x0020000,
  EDIT: 0x0040000,
  MULTISELECT: 0x0200000,
  DONOTSPELLCHECK: 0x0400000,
  DONOTSCROLL: 0x0800000,
  COMB: 0x1000000,
  RICHTEXT: 0x2000000,
};

const AnnotationFlag = {
  INVISIBLE: 0x01,
  HIDDEN: 0x02,
  PRINT: 0x04,
  NOVIEW: 0x20,
};

const hasFlag = (flags: number | undefined, flag: number) =>
  // eslint-disable-next-line no-bitwise
  flags !== undefined && (flags & flag) === flag;

const decodeFieldFlags = (fieldFlags?: number) => {
  if (!Number.isInteger(fieldFlags)) return {};
  return {
    readOnly: hasFlag(fieldFlags, AnnotationFieldFlag.READONLY),
    required: hasFlag(fieldFlags, AnnotationFieldFlag.REQUIRED),
    noExport: hasFlag(fieldFlags, AnnotationFieldFlag.NOEXPORT),
    multiline: hasFlag(fieldFlags, AnnotationFieldFlag.MULTILINE),
    password: hasFlag(fieldFlags, AnnotationFieldFlag.PASSWORD),
    richText: hasFlag(fieldFlags, AnnotationFieldFlag.RICHTEXT),
    doNotScroll: hasFlag(fieldFlags, AnnotationFieldFlag.DONOTSCROLL),
    doNotSpellCheck: hasFlag(fieldFlags, AnnotationFieldFlag.DONOTSPELLCHECK),
    comb: hasFlag(fieldFlags, AnnotationFieldFlag.COMB),
    isCombo: hasFlag(fieldFlags, AnnotationFieldFlag.COMBO),
    editable: hasFlag(fieldFlags, AnnotationFieldFlag.EDIT),
    multiSelect: hasFlag(fieldFlags, AnnotationFieldFlag.MULTISELECT),
  };
};

interface PDFDocumentProxy {
  getPage(pageNumber: number): Promise<PDFPageProxy>;
}

interface PDFPageProxy {
  getAnnotations(): Promise<PDFAnnotation[]>;
  getViewport(options: { scale: number }): PDFViewport;
}

interface PDFViewport {
  height: number;
}

interface PDFAnnotation {
  subtype: string;
  rect: [number, number, number, number];
  fieldType?: string;
  fieldValue?: string | boolean | string[];
  fieldName?: string;
  checkBox?: boolean;
  radioButton?: boolean;
  defaultAppearance?: string; // DA string contains font info like '/Helv 12 Tf'
  multiLine?: boolean;
  options?: Array<{ exportValue: string; displayValue: string } | string>;
  combo?: boolean; // true for dropdown, false for listbox
  edit?: boolean; // combo allows freeform input
  multiSelect?: boolean;
  maxLen?: number; // Maximum length for text fields (required for comb fields)
  textAlignment?: number; // 0=left, 1=center, 2=right
  readOnly?: boolean; // Read-only flag
  required?: boolean;
  noExport?: boolean;
  hidden?: boolean;
  printable?: boolean;
  password?: boolean;
  richText?: boolean;
  doNotScroll?: boolean;
  doNotSpellCheck?: boolean;
  comb?: boolean; // Comb formatting (ISO 32000: divides field into maxLen equal positions)
  buttonValue?: string; // Export value for individual radio button (appearance state name)
  fieldFlags?: number;
  annotationFlags?: number;
}

export const extractFormFieldsFromAnnotations = (
  annotations: PDFAnnotation[],
  pageNumber: number,
  baseViewportHeight: number,
): FormField[] => {
  const widgets = annotations.filter(
    (annot: PDFAnnotation) => annot.subtype === 'Widget',
  );

  // Pre-analyze: find checkbox field names that appear multiple times.
  // Multiple checkbox widgets sharing the same fieldName form a mutually-exclusive
  // group (functionally radio buttons). Reclassifying them as radios ensures:
  //  - correct mutual-exclusion in state management (handleFieldChange)
  //  - correct PDF export (set V + widget AS instead of check/uncheck on
  //    the same PDFCheckBox, which would let the last uncheck win)
  //  - correct defaultValue (compare fieldValue vs buttonValue per widget)
  const checkboxNameCounts = new Map<string, number>();
  widgets.forEach((annot) => {
    if (annot.fieldType === 'Btn' && annot.checkBox && annot.fieldName) {
      checkboxNameCounts.set(
        annot.fieldName,
        (checkboxNameCounts.get(annot.fieldName) || 0) + 1,
      );
    }
  });
  const checkboxGroupNames = new Set<string>();
  checkboxNameCounts.forEach((count, name) => {
    if (count > 1) checkboxGroupNames.add(name);
  });

  return widgets.map((annot: PDFAnnotation, index: number) => {
    const { rect } = annot;
    const [x1, y1, x2, y2] = rect;

    const canvasX = x1;
    const canvasY = baseViewportHeight - y2;
    const canvasWidth = x2 - x1;
    const canvasHeight = y2 - y1;

    let fieldType: 'text' | 'checkbox' | 'radio' | 'dropdown' | 'textarea' =
      'text';
    let defaultValue: string | boolean | string[] = '';
    let options: string[] | undefined;
    let radioGroup: string | undefined;
    let exportValue: string | undefined;
    let multiline: boolean | undefined;
    let maxLength: number | undefined;
    let alignment: 'left' | 'center' | 'right' | undefined;
    const fieldFlagsRaw = Number.isInteger(annot.fieldFlags)
      ? annot.fieldFlags
      : undefined;
    const decodedFlags = decodeFieldFlags(fieldFlagsRaw);
    const annotationFlagsRaw = Number.isInteger(annot.annotationFlags)
      ? annot.annotationFlags
      : undefined;
    const hiddenFromFlags =
      annotationFlagsRaw !== undefined
        ? hasFlag(annotationFlagsRaw, AnnotationFlag.HIDDEN) ||
          hasFlag(annotationFlagsRaw, AnnotationFlag.INVISIBLE) ||
          hasFlag(annotationFlagsRaw, AnnotationFlag.NOVIEW)
        : undefined;
    const printableFromFlags =
      annotationFlagsRaw !== undefined
        ? hasFlag(annotationFlagsRaw, AnnotationFlag.PRINT) && !hiddenFromFlags
        : undefined;

    const readOnly = annot.readOnly ?? decodedFlags.readOnly;
    const required = annot.required ?? decodedFlags.required;
    const noExport = annot.noExport ?? decodedFlags.noExport;
    const hidden =
      typeof annot.hidden === 'boolean' ? annot.hidden : hiddenFromFlags;
    const printable =
      typeof annot.printable === 'boolean'
        ? annot.printable
        : printableFromFlags;
    const password = annot.password ?? decodedFlags.password;
    const richText = annot.richText ?? decodedFlags.richText;
    const doNotScroll = annot.doNotScroll ?? decodedFlags.doNotScroll;
    const doNotSpellCheck =
      annot.doNotSpellCheck ?? decodedFlags.doNotSpellCheck;
    const comb = annot.comb ?? decodedFlags.comb;
    const isCombo = annot.combo ?? decodedFlags.isCombo;
    const multiSelect = annot.multiSelect ?? decodedFlags.multiSelect;
    const editable = annot.edit ?? decodedFlags.editable;

    if (annot.fieldType === 'Tx') {
      const isMultiline = annot.multiLine ?? decodedFlags.multiline;
      if (isMultiline) {
        fieldType = 'textarea';
        multiline = true;
      } else {
        fieldType = 'text';
      }
      defaultValue = Array.isArray(annot.fieldValue)
        ? annot.fieldValue.join(', ')
        : annot.fieldValue || '';

      maxLength = annot.maxLen;

      if (annot.textAlignment === 1) {
        alignment = 'center';
      } else if (annot.textAlignment === 2) {
        alignment = 'right';
      } else {
        alignment = 'left';
      }
    } else if (annot.fieldType === 'Btn' && annot.checkBox) {
      const isCheckboxGroup = checkboxGroupNames.has(annot.fieldName || '');

      if (isCheckboxGroup) {
        // Multiple checkbox widgets share the same fieldName → treat as
        // a radio group so that mutual-exclusion and export work correctly.
        fieldType = 'radio';
        radioGroup = annot.fieldName || `radio-group-${pageNumber}`;
        exportValue = annot.buttonValue || undefined;
        // Compare group's selected value with this widget's buttonValue
        // to determine whether THIS specific widget is the selected one.
        defaultValue =
          annot.fieldValue != null &&
          annot.fieldValue !== '' &&
          annot.fieldValue !== 'Off' &&
          annot.fieldValue !== false &&
          annot.fieldValue === annot.buttonValue;
      } else {
        // Independent checkbox (unique fieldName)
        fieldType = 'checkbox';
        // PDF spec (ISO 32000): unchecked state is the Name /Off (exact,
        // case-sensitive).  Checked state can be ANY other Name value
        // (e.g. /On, /Yes, /1, /True, or custom export values — including
        // non-standard casing like /off which is a distinct Name from /Off).
        // Compare with the exact string 'Off' only.
        defaultValue =
          annot.fieldValue != null &&
          annot.fieldValue !== '' &&
          annot.fieldValue !== 'Off' &&
          annot.fieldValue !== false;
      }
    } else if (annot.fieldType === 'Btn' && annot.radioButton) {
      fieldType = 'radio';
      radioGroup = annot.fieldName || `radio-group-${pageNumber}`;
      exportValue = annot.buttonValue || undefined;
      // Radio buttons share a group field: annot.fieldValue is the group's
      // selected value (e.g. "Choice1"), the same for every widget in the
      // group.  Compare it with this widget's own buttonValue to decide
      // whether THIS specific radio is the selected one.
      defaultValue =
        annot.fieldValue != null &&
        annot.fieldValue !== '' &&
        annot.fieldValue !== 'Off' &&
        annot.fieldValue !== false &&
        annot.fieldValue === annot.buttonValue;
    } else if (annot.fieldType === 'Ch') {
      fieldType = 'dropdown';
      defaultValue = Array.isArray(annot.fieldValue)
        ? annot.fieldValue
        : annot.fieldValue || '';

      if (annot.options && Array.isArray(annot.options)) {
        options = annot.options.map((opt) =>
          typeof opt === 'string' ? opt : opt.displayValue || opt.exportValue,
        );
      } else {
        options = [];
      }
    }

    let fontSize = 12; // Default fallback (12pt is common in PDF forms)
    let fontFamily: string | undefined;
    if (
      annot.defaultAppearance &&
      (fieldType === 'text' || fieldType === 'textarea')
    ) {
      const fontMatch = annot.defaultAppearance.match(
        /\/(\w+)\s+(\d+(?:\.\d+)?)\s+Tf/,
      );
      if (fontMatch) {
        const pdfFontName = fontMatch[1];
        const fontSizeStr = fontMatch[2];

        if (pdfFontName) {
          fontFamily = mapPDFFont(pdfFontName);
        }
        if (fontSizeStr) {
          const extractedSize = parseFloat(fontSizeStr);
          fontSize = extractedSize > 0 ? extractedSize : canvasHeight * 0.7;
        }
      }
    }

    return {
      id: `field-${pageNumber}-${index}`,
      type: fieldType,
      name: annot.fieldName || `field-${index}`,
      value: defaultValue,
      rect: [canvasX, canvasY, canvasWidth, canvasHeight],
      pageNumber,
      fontSize:
        fieldType === 'text' || fieldType === 'textarea' ? fontSize : undefined,
      fontFamily:
        fieldType === 'text' || fieldType === 'textarea'
          ? fontFamily
          : undefined,
      options,
      radioGroup,
      exportValue,
      multiline,
      maxLength,
      alignment,
      readOnly,
      required,
      noExport,
      hidden,
      printable,
      password,
      richText,
      doNotScroll,
      doNotSpellCheck,
      comb,
      isCombo,
      multiSelect,
      editable,
      fieldFlagsRaw,
    };
  });
};

/**
 * Hook to extract form fields from PDF annotations
 * @param pdfDocument PDF.js document proxy
 * @param currentPage Current page number
 * @param scale Current zoom scale
 */
export const usePDFFormFields = (
  pdfDocument: PDFDocumentProxy | null,
  currentPage: number,
  scale: number,
): FormField[] => {
  const [formFields, setFormFields] = useState<FormField[]>([]);

  useEffect(() => {
    if (!pdfDocument) return;

    const extractFields = async () => {
      try {
        const page = await pdfDocument.getPage(currentPage);
        const annotations = await page.getAnnotations();
        const baseViewport = page.getViewport({ scale: 1.0 });

        const fields = extractFormFieldsFromAnnotations(
          annotations,
          currentPage,
          baseViewport.height,
        );

        setFormFields(fields);
      } catch (error) {
        logger.error('Error extracting form fields:', error);
        setFormFields([]);
      }
    };

    extractFields();
  }, [pdfDocument, currentPage, scale]);

  return formFields;
};
